import PageScaffold from '../../components/PageScaffold';

import { useContext, useEffect, useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { Flex, Select, Tooltip } from 'antd';
import UsersList from '../../components/users_page/UsersList';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import PlansContext from '../../contexts/data/PlansContext';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { UserRole, EProductType, productTypeAsString } from '@leedpulse-monorepo/enums';
import { renderUserRoleOption } from '@leedpulse-monorepo/enums-ui';
import HttpClient from '../../../core/HttpClient';

export const USERS_PAGE_PATH = '/users';
export const USERS_PAGE_TITLE = 'Utilisateurs';

const CHANNELS = [
  { label: 'VoIP', value: 'voip' },
  { label: 'GSM', value: 'gsm' },
];

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface UsersPageProps {}

const UsersPage: React.FC<UsersPageProps> = (_) => {
  const authContext = useContext(AuthenticationContext);
  const plansContext = useContext(PlansContext);

  const [users, setUsers] = useState<Record<string, any>[]>([]);
  const [page, setPage] = useState<number>(0);
  const [nbHits, setNbHits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [query, setQuery] = useState('');
  const [hitsPerPage, setHitsPerPage] = useState<number>(20);
  const [roleConstraint, setRoleConstraint] = useState<UserRole | undefined>(undefined);
  const [products, setProducts] = useState<EProductType[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [plans, setPlans] = useState<string[]>([]);

  useEffect(() => {
    plansContext.fetch();
  }, []);

  async function searchUsers(pageToFetch = 0, newHitsPerPage = hitsPerPage) {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        q: query,
        page: String(pageToFetch),
        hitsPerPage: String(newHitsPerPage),
      });
      if (roleConstraint) params.append('role', roleConstraint);
      products.forEach((product) => params.append('products[]', product));
      channels.forEach((channel) => params.append('channels[]', channel));
      plans.forEach((plan) => params.append('plans[]', plan));

      const json = await HttpClient.get(
        `/algolia/search/backoffice/users?${params.toString()}`,
        authContext.authentication?.token
      );
      setUsers(json.hits);
      setNbHits(json.nbHits);
      setPage(pageToFetch);
    } catch (err) {
      console.error('[Algolia] erreur :', err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, roleConstraint, products, channels, plans]);

  return (
    <PageScaffold
      title={USERS_PAGE_TITLE + ' (' + formatNumber(nbHits) + ')'}
      subtitle="Gérez les utilisateurs de l'application"
      breadcrumbItems={[{ title: 'Utilisateurs' }]}
      controlsSettings={{
        searchLabel: 'Nom, prénom, téléphone ou email',
        onSearch: (value) => setQuery(value ?? ''),
        isLoading: false,
        onSearchCancel: () => setQuery(''),
        actionButtons: [
          {
            label: 'Rafraîchir',
            type: 'primary',
            props: {
              icon: <ReloadOutlined />,
              onClick: () => searchUsers(page),
            },
          },
        ],
      }}
      filterSettings={{
        placeholder: 'Filtrer par rôle',
        clearable: true,
        onFilter: (filter) => setRoleConstraint(filter as UserRole | undefined),
        values: Object.values(UserRole).map((role) => ({
          label: renderUserRoleOption(role) as string,
          value: role,
        })),
        selectedValue: roleConstraint,
        style: {
          width: '15.625rem',
        },
      }}
    >
      <Flex gap="0.5rem" wrap="wrap" style={{ marginBottom: '1rem' }}>
        <Tooltip title="Filtrer par produit">
          <Select
            style={{ width: 250 }}
            placeholder="Filtrer par produit"
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            value={products}
            options={[
              { label: productTypeAsString(EProductType.LEEDPULSE), value: EProductType.LEEDPULSE },
              { label: productTypeAsString(EProductType.LEEDFLOW), value: EProductType.LEEDFLOW },
            ]}
            onChange={(value) => setProducts(value)}
          />
        </Tooltip>
        <Tooltip title="Filtrer par canal">
          <Select
            style={{ width: 250 }}
            placeholder="Filtrer par canal"
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            value={channels}
            options={CHANNELS}
            onChange={(value) => setChannels(value)}
          />
        </Tooltip>
        <Tooltip title="Filtrer par plan">
          <Select
            style={{ width: 250 }}
            placeholder="Filtrer par plan"
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            value={plans}
            options={plansContext.items.map((plan) => ({ label: plan.name, value: plan.id }))}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => setPlans(value)}
          />
        </Tooltip>
      </Flex>
      <UsersList
        users={users}
        loading={isLoading}
        page={page}
        nbHits={nbHits}
        hitsPerPage={hitsPerPage}
        onPageChange={(p) => searchUsers(p)}
        onPageSizeChange={(newPageSize) => {
          setHitsPerPage(newPageSize);
          searchUsers(0, newPageSize);
        }}
      />
    </PageScaffold>
  );
};

export default UsersPage;
